import { maxBy, minBy } from 'lodash-es';
import {
  CheckOperator,
  MemoryCheckValueType,
  MemoryDTO,
  MemoryFormDTO,
  MemoryValue,
  StepCheck,
  StepCheckOperatorType,
  StepCheckSwitch,
} from '../../components/pages/Book/MemoryBank/memoryBankTypes';
import { convertMemoryDTOToFormDTO } from '../../components/pages/Book/MemoryBank/memoryBankUtils';
import { getMemorySlotVariableLink } from '../../components/utils/choiceMemoryUtils';
import { bugTracker } from '../bugTracker/BugTrackerService';
import { MemoryBankService } from '../memoryBankService/MemoryBankService';
import { ChoiceMemoryStack } from './types/ChoiceMemoryStack';

export class ChoiceMemory {
  private readonly choiceMemoryStack: ChoiceMemoryStack[];
  private variables: { [ key: string ]: MemoryFormDTO};
  private memoryBankService: MemoryBankService;

  constructor() {
    this.memoryBankService = new MemoryBankService();
    this.choiceMemoryStack = [];
    this.variables = {};
  }

  async fetchVariables(bookId: number) {
    const memoryBank = await this.memoryBankService.fetchMemoryBank(bookId);
    memoryBank.forEach((variable: MemoryDTO) => {
      this.variables[variable.name] = convertMemoryDTOToFormDTO(variable);
    });
    this.setCurrentVariables(this.variables);
  }

  push(stackItem: ChoiceMemoryStack) {
    this.choiceMemoryStack.push(stackItem);
  }

  pop(): ChoiceMemoryStack | undefined {
    const removerItem = this.choiceMemoryStack.pop();
    return removerItem;
  }

  get last(): ChoiceMemoryStack | undefined {
    return { ...this.choiceMemoryStack[this.choiceMemoryStack.length - 1] };
  }

  get currentVariables(): { [ key: string ]: MemoryFormDTO} {
    return this.variables;
  }

  saveCurrentVariables() {
    const variables = Object.values(this.variables).map((variable) => ({
      name: variable.name,
      value: variable.value,
      id: variable.id,
    }));
    localStorage.setItem('choiceMemoryVariables', JSON.stringify(variables));
  }

  setCurrentVariables(variables: { [ key: string ]: MemoryFormDTO}) {
    this.variables = variables;
    this.saveCurrentVariables();
  }

  getVariableById(id: number) {
    const variable = Object.values(this.variables)
      .find((variableItem) => Number(variableItem.id) === Number(id));
    if (variable) {
      return variable;
    }
    return undefined;
  }

  setVariableById(id: number, value: MemoryFormDTO) {
    const variable = this.getVariableById(id);
    if (variable) {
      this.variables = { ...this.variables, [variable.name]: value };
    }
  }

  extractVariablesFromString(text: string) {
    const variableRegex = /\{([A-Za-z0-9_.]+)\}/g;
    const matches = text?.match(variableRegex);
    if (matches) {
      return matches.map((match) => match.substring(1, match.length - 1));
    }
    return [];
  }

  getVariableByName(varName: string) {
    const variable = this.variables[varName];
    if (variable) {
      return variable;
    }
    return undefined;
  }

  setValueByVarName(varName: string, value: string | number) {
    this.variables[varName].value = value;
  }

  getValueById(id: number): MemoryValue | undefined {
    const variable = this.getVariableById(id);
    if (variable) {
      const isNumber = variable.type === 'number';
      return isNumber ? Number(variable.value) : variable.value;
    }
    return undefined;
  }

  setValueById(id: number, value: string | number) {
    const variable = this.getVariableById(id);
    if (variable) {
      this.variables = { ...this.variables, [variable.name]: { ...variable, value } };
    }
    this.setCurrentVariables(this.variables);
  }

  // This method is used in onBeforeTextRender because onBeforeTextRender
  // is called before onNavHistoryStackChanged
  getVariableFromLastStackItem(stepId: number, variableName: string) {
    if (this.choiceMemoryStack.length <= 1) {
      return undefined;
    }
    const stackItem = this.choiceMemoryStack[this.choiceMemoryStack.length - 2];
    if (stackItem) {
      return stackItem.variables[variableName];
    }
    return undefined;
  }

  getGotoBranchIdByCheck(check: StepCheck, gotoSwitches: StepCheckSwitch[]): number | undefined {
    const checkOperator = CheckOperator[check.operator];
    if (!checkOperator) {
      bugTracker().reportError({ name: '[AT-CM]', message: `Operator ${check.operator} is not found` });
      return undefined;
    }

    // PlayerHas is achievement-based (variableId is 0, value is reward ids), so it
    // cannot be resolved against memory state here — the memory-slot path would
    // throw on the missing variable. It is resolved earlier in onGoForwardStack
    // via an author Yes/No prompt; reaching this method means that handler was
    // skipped. Return undefined defensively instead of throwing.
    if (checkOperator.isPlayerHas) {
      bugTracker().reportError({ name: '[AT-CM]', message: `PlayerHas check reached getGotoBranchIdByCheck without a confirm handler (stepCheck variableId=${check.variableId})` });
      return undefined;
    }

    if (checkOperator.isConditionList) {
      try {
        return this._getGotoBranchByConditionList(check, gotoSwitches);
      } catch (error) {
        bugTracker().reportError({ name: '[AT-CM]', message: 'Error in getGotoBranchIdByCheck (conditionList)', cause: error });
        return undefined;
      }
    }

    if (checkOperator.isMultiSelect) {
      try {
        return this._getGotoBranchByMultiSelectedMemory(check, gotoSwitches);
      } catch (error) {
        bugTracker().reportError({ name: '[AT-CM]', message: 'Error in getGotoBranchIdByCheck', cause: error });
        return undefined;
      }
    }

    try {
      return this._getGotoBranchByOneSelectedMemory(check, gotoSwitches);
    } catch (error) {
      bugTracker().reportError({ name: '[AT-CM]', message: 'Error in getGotoBranchIdByCheck', cause: error });
      return undefined;
    }
  }

  private _getGotoBranchByMultiSelectedMemory(check: StepCheck, gotoSwitches: StepCheckSwitch[]) {
    const checkValue = Array.isArray(check.value) ? '' : (check.value ?? '');
    const memoryLinkValue = getMemorySlotVariableLink(checkValue);
    if (!memoryLinkValue) {
      throw new Error('MemoryLinkValue is not found');
    }

    if (memoryLinkValue.type !== MemoryCheckValueType.VariableArray) {
      throw new Error('MemoryLinkValue is not an array');
    }

    const memories = memoryLinkValue.variableId.map((id) => this.getVariableById(Number(id)));

    switch (check.operator) {
      case StepCheckOperatorType.Min: {
        const minMemory = minBy(memories, (memory) => Number(memory?.value));
        const minSwitch = gotoSwitches.find((gotoSwitch) => Number(gotoSwitch.value) === minMemory?.id);
        return minSwitch ? Number(minSwitch.gotoBranchId) : undefined;
      }
      case StepCheckOperatorType.Max: {
        const maxMemory = maxBy(memories, (memory) => Number(memory?.value));
        const maxSwitch = gotoSwitches.find((gotoSwitch) => Number(gotoSwitch.value) === maxMemory?.id);
        return maxSwitch ? Number(maxSwitch.gotoBranchId) : undefined;
      }
      default:
        throw new Error(`Unknown operator ${check.operator}`);
    }
  }

  private _getGotoBranchByConditionList(check: StepCheck, gotoSwitches: StepCheckSwitch[]) {
    const mainVariable = this.getVariableById(check.variableId);
    
    if (!mainVariable) {
      throw new Error(`Variable ${check.variableId} is not found`);
    }
    
    const mainValue = this.getValueById(check.variableId);
    
    if (mainValue === undefined) {
      throw new Error(`Variable ${check.variableId} value is not found`);
    }

    const isNumber = mainVariable.type === 'number';
    const comparedValue = isNumber ? Number(mainValue) : String(mainValue).toLowerCase();

    // Iterate through switch items in order - first match wins
    for (const switchItem of gotoSwitches) {
      // If no operator, this is the "otherwise" case - always matches
      if (!switchItem.operator) {
        return Number(switchItem.gotoBranchId);
      }

      // Get the comparison value from either number (literal) or variable reference
      // Support both frontend format (intValue/varValue) and backend format (number/variable)
      let checkValue: number | string;
      
      const literalValue = switchItem.intValue ?? switchItem.number;
      const variableId = switchItem.varValue ?? switchItem.variable;
      
      if (literalValue !== undefined && literalValue !== null && literalValue !== '') {
        // Using a literal number/string value
        checkValue = isNumber ? Number(literalValue) : String(literalValue).toLowerCase();
      } else if (variableId !== undefined && variableId !== null && variableId !== 0) {
        // Using another variable's value
        const compareVariable = this.getVariableById(Number(variableId));
        
        if (!compareVariable) {
          throw new Error(`Variable ${variableId} is not found`);
        }
        
        const compareValue = this.getValueById(Number(variableId));
        
        if (compareValue === undefined) {
          throw new Error(`Variable ${variableId} value is not found`);
        }
        
        checkValue = isNumber ? Number(compareValue) : String(compareValue).toLowerCase();
      } else {
        // No comparison value provided, skip this condition
        continue;
      }

      // Perform the comparison based on operator
      let conditionMet = false;
      
      switch (switchItem.operator) {
        case StepCheckOperatorType.Equal:
          conditionMet = comparedValue === checkValue;
          break;
        case StepCheckOperatorType.NotEqual:
          conditionMet = comparedValue !== checkValue;
          break;
        case StepCheckOperatorType.Greater:
          conditionMet = comparedValue > checkValue;
          break;
        case StepCheckOperatorType.AtLeast:
          conditionMet = comparedValue >= checkValue;
          break;
        case StepCheckOperatorType.Less:
          conditionMet = comparedValue < checkValue;
          break;
        case StepCheckOperatorType.AtMost:
          conditionMet = comparedValue <= checkValue;
          break;
        default:
          throw new Error(`Unknown operator ${switchItem.operator}`);
      }

      if (conditionMet) {
        return Number(switchItem.gotoBranchId);
      }
    }

    // No condition matched and no otherwise case found
    return undefined;
  }

  private _getGotoBranchByOneSelectedMemory(check: StepCheck, gotoSwitches: StepCheckSwitch[]) {
    const comparedVariable = this.getVariableById(check.variableId);

    if (!comparedVariable) {
      throw new Error(`Variable ${check.variableId} is not found`);
    }

    const variableValue = this.getValueById(check.variableId);

    if (variableValue === undefined) {
      throw new Error(`Variable ${check.variableId} value is not found`);
    }

    // Exclude number[] (used by playerHas) - this method only handles memory slot checks
    const checkValueForLink = Array.isArray(check.value) ? '' : (check.value ?? '');
    const memoryLink = getMemorySlotVariableLink(checkValueForLink);
    const memoryLinkValue = this.getValueById(Number(memoryLink?.variableId));

    if (memoryLink !== undefined && memoryLinkValue === undefined) {
      throw new Error(`Variable ${memoryLink.variableId} value is not found`);
    }

    const slotCheckValue = memoryLink
      ? this.getValueById(Number(memoryLink.variableId)) ?? ''
      : check.value;

    const isNumber = comparedVariable.type === 'number';

    if (isNumber && Number.isNaN(Number(slotCheckValue))) {
      throw new Error(`Check value is not a number in variableId: ${check.variableId}`);
    }

    if (isNumber && Number.isNaN(Number(variableValue))) {
      throw new Error(`Check value is not a number in variableId: ${check.variableId}`);
    }

    const checkValue = isNumber ? Number(slotCheckValue) : String(slotCheckValue).toLowerCase();
    const comparedValue = isNumber ? Number(variableValue) : String(variableValue).toLowerCase();

    const gotoBranchIdTrue = gotoSwitches.find(
      (gotoSwitch: StepCheckSwitch) => String(gotoSwitch.value) === 'true',
    );
    const gotoBranchIdFalse = gotoSwitches.find(
      (gotoSwitch: StepCheckSwitch) => String(gotoSwitch.value) === 'false',
    );

    switch (check.operator) {
      case StepCheckOperatorType.Equal: {
        if (comparedValue === checkValue) {
          return gotoBranchIdTrue ? Number(gotoBranchIdTrue.gotoBranchId) : undefined;
        }
        return gotoBranchIdFalse ? Number(gotoBranchIdFalse.gotoBranchId) : undefined;
      }
      case StepCheckOperatorType.NotEqual: {
        if (comparedValue !== checkValue) {
          return gotoBranchIdTrue ? Number(gotoBranchIdTrue.gotoBranchId) : undefined;
        }
        return gotoBranchIdFalse ? Number(gotoBranchIdFalse.gotoBranchId) : undefined;
      }
      case StepCheckOperatorType.Greater: {
        if (comparedValue > checkValue) {
          return gotoBranchIdTrue ? Number(gotoBranchIdTrue.gotoBranchId) : undefined;
        }
        return gotoBranchIdFalse ? Number(gotoBranchIdFalse.gotoBranchId) : undefined;
      }
      case StepCheckOperatorType.AtLeast: {
        if (comparedValue >= checkValue) {
          return gotoBranchIdTrue ? Number(gotoBranchIdTrue.gotoBranchId) : undefined;
        }
        return gotoBranchIdFalse ? Number(gotoBranchIdFalse.gotoBranchId) : undefined;
      }
      case StepCheckOperatorType.Less: {
        if (comparedValue < checkValue) {
          return gotoBranchIdTrue ? Number(gotoBranchIdTrue.gotoBranchId) : undefined;
        }
        return gotoBranchIdFalse ? Number(gotoBranchIdFalse.gotoBranchId) : undefined;
      }
      case StepCheckOperatorType.AtMost: {
        if (comparedValue <= checkValue) {
          return gotoBranchIdTrue ? Number(gotoBranchIdTrue.gotoBranchId) : undefined;
        }
        return gotoBranchIdFalse ? Number(gotoBranchIdFalse.gotoBranchId) : undefined;
      }
      default:
        throw new Error(`Unknown operator ${check.operator}`);
    }
  }
}
